import FolderIcon from '@/assets/icons/folder.svg?react'
import { Checkbox, EmptyView, Trans } from '@/components'
import { formatBytes, formatDatePreset, formatSpeed } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { FILE_STATUS } from '../core/hooks/useUpload'
import { IFile } from '../fs'
import * as fs from '../fs'
import { FileIcon } from './FileIcon'
import { HoverActions } from './HoverActions'

interface UploadTask {
  name: string
  size: number
  loaded?: number
  status: number
  speed?: number
}

interface FileTableProps {
  /**
   * List of files in current directory
   */
  files: IFile[]
  /**
   * Files currently being uploaded
   */
  uploadingTasks: UploadTask[]
  /**
   * Currently selected files
   */
  selectedRows: IFile[]
  /**
   * Current domain for download operations
   */
  currentDomain: number
  /**
   * Whether editing operations should be disabled (e.g., order expired)
   * @default false
   */
  disableEdit?: boolean
  /**
   * Callback when file selection changes
   */
  onSelectionChange: (file: IFile, checked: boolean) => void
  /**
   * Callback when file/folder is clicked
   * For folders: should navigate to folder
   * For files: should toggle selection
   */
  onFileClick: (file: IFile) => void
  /**
   * Callback when move button clicked
   */
  onMove: (file: IFile) => void
  /**
   * Callback when rename button clicked
   */
  onRename: (file: IFile) => void
  /**
   * Callback when delete button clicked
   */
  onDelete: (file: IFile) => void
  /**
   * Optional callback for custom download implementation
   * If not provided, uses default fs.download
   */
  onDownload?: (file: IFile) => void
  /**
   * Callback when select all checkbox is toggled
   */
  onSelectAll?: (checked: boolean) => void
}

/**
 * Table component for displaying files and folders
 * Supports file selection, uploading tasks, and file operations
 */
export function FileTable({
  files,
  uploadingTasks,
  selectedRows,
  currentDomain,
  disableEdit = false,
  onSelectionChange,
  onFileClick,
  onMove,
  onRename,
  onDelete,
  onDownload,
  onSelectAll,
}: FileTableProps) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th className="w-[52px] rounded-l-lg">
            {onSelectAll && (
              <Checkbox
                checked={files.length > 0 && selectedRows.length === files.length}
                onChange={onSelectAll}
                inputClassName="w-4 h-4"
                checkedBgColor="bg-btn-secondary"
                checkedBorderColor="border-none"
                checkIconColor="text-white"
              />
            )}
          </th>
          <th>
            <Trans i18nKey="data.tableHeaderName">Name</Trans>
          </th>
          <th className="w-[120px]">
            <Trans i18nKey="data.tableHeaderSize">Size</Trans>
          </th>
          <th className="w-[200px] rounded-r-lg">
            <Trans i18nKey="data.tableHeaderLastModified">Last Modified</Trans>
          </th>
        </tr>
      </thead>
      <tbody>
        {uploadingTasks.length === 0 && files.length === 0 && (
          <tr>
            <td colSpan={4} className="border-none">
              <EmptyView title={<Trans i18nKey="common.emptyViewNoDataMsg" />} />
            </td>
          </tr>
        )}

        {/* Uploading tasks */}
        {uploadingTasks.map((item, idx) => {
          const percentage =
            item.loaded && item.size > 0 ? Math.min(100, (100 * item.loaded) / item.size) : 0

          let barColor = 'bg-btn-secondary'
          if (item.status === FILE_STATUS.SUCCESS || percentage >= 100) {
            barColor = 'bg-btn-secondary'
          } else if (item.status === FILE_STATUS.FAILED) {
            barColor = 'bg-error'
          } else if (item.status === FILE_STATUS.PAUSED) {
            barColor = 'bg-btn-secondary'
          }

          return (
            <tr key={idx} className="group hover:bg-panel-gray-100">
              <td className="t-sm">
                <Checkbox disabled />
              </td>
              <td className="max-w-0 t-sm">
                <span className="block truncate" title={item.name}>
                  {item.name}
                </span>
              </td>
              <td className="t-sm">{formatBytes(item.size)}</td>
              <td className="text-tertiary t-sm">
                <div className="flex flex-col gap-1">
                  <div className="flex justify-start items-center space-x-2">
                    {item.status === FILE_STATUS.PAUSED ? (
                      <span>Paused {percentage.toFixed(0)}%</span>
                    ) : (
                      <>
                        <span>{item.speed ? `${formatSpeed(item.speed)} ` : ''}</span>
                        <span>{percentage > 0 ? `${percentage.toFixed(0)}%` : 'Starting'}</span>
                      </>
                    )}
                  </div>
                  <div className="w-full h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all duration-300 ease-out',
                        barColor,
                      )}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              </td>
            </tr>
          )
        })}

        {/* Regular files */}
        {files.map((file, idx) => (
          <tr key={idx} className="group hover:bg-panel-gray-100">
            <td className="w-[52px] t-sm" onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={selectedRows.some((row) => row.id === file.id)}
                onChange={(checked) => onSelectionChange(file, checked)}
                inputClassName="w-4 h-4"
                checkedBgColor="bg-btn-secondary"
                checkedBorderColor="border-none"
                checkIconColor="text-white"
              />
            </td>
            <td className="max-w-0 cursor-pointer relative t-sm" onClick={() => onFileClick(file)}>
              {file.isDir ? (
                <span className="flex items-center gap-x-2 min-w-0 group-hover:pr-[140px]">
                  <FolderIcon className="h-5 w-5 shrink-0 text-warning" />
                  <span className="truncate">{file.name}</span>
                </span>
              ) : (
                <span className="flex items-center gap-x-2 min-w-0 group-hover:pr-[140px]">
                  <FileIcon ext={file.ext} className="shrink-0" />
                  <span className="truncate">{file.name}</span>
                </span>
              )}
              <HoverActions
                file={file}
                onDownload={() =>
                  onDownload
                    ? onDownload(file)
                    : fs.download({ path: file.path, domain: currentDomain })
                }
                onMove={() => onMove(file)}
                onRename={() => onRename(file)}
                onDelete={() => onDelete(file)}
                disableEdit={disableEdit}
              />
            </td>
            <td className="t-sm">{formatBytes(file.size)}</td>
            <td className="t-sm text-tertiary">
              {formatDatePreset(file.updateTime * 1000, 'second')}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
